import { TRAVEL_STYLES } from "../data/seedData";

// Identity-defining dimensions weigh more than logistics.
export const DIMENSION_WEIGHTS = {
  interest: 1.5,
  eats: 1.25,
  group: 1,
  stays: 1,
  transport: 0.75,
};

// Tier thresholds on the 0-100 score
export const TIER_KINDRED = 70;
export const TIER_SIMILAR = 40;

// Shrinkage constant for the behavioral blend: at k posts, tags and behavior weigh equally
const BEHAVIOR_K = 5;
// Category cosine is noise below this many posts per user
const MIN_POSTS_FOR_CATEGORIES = 3;

const TAG_TO_DIMENSION = {};
for (const [dim, tags] of Object.entries(TRAVEL_STYLES)) {
  for (const tag of tags) TAG_TO_DIMENSION[tag] = dim;
}

// Dimensions ordered by weight (desc) so shared styles surface most-meaningful first
const DIMENSIONS_BY_WEIGHT = Object.keys(DIMENSION_WEIGHTS).sort(
  (a, b) => DIMENSION_WEIGHTS[b] - DIMENSION_WEIGHTS[a]
);

function groupByDimension(styles) {
  const grouped = {};
  for (const tag of styles || []) {
    const dim = TAG_TO_DIMENSION[tag];
    if (!dim) continue;
    if (!grouped[dim]) grouped[dim] = [];
    grouped[dim].push(tag);
  }
  return grouped;
}

// Per-dimension Dice coefficient, weighted mean over dimensions BOTH users declared.
// Dividing per dimension avoids Jaccard's enthusiasm penalty (a many-tag user no
// longer drags the score down) and keeps skipped dimensions from deflating the union.
// Returns 0-100, or null when either side is empty or fewer than 2 dimensions overlap
// (a single mutual dimension is a coin flip, not a signal).
export function calcSimilarity(myStyles, theirStyles) {
  if (!myStyles?.length || !theirStyles?.length) return null;
  const mine = groupByDimension(myStyles);
  const theirs = groupByDimension(theirStyles);
  const mutualDims = DIMENSIONS_BY_WEIGHT.filter(
    (d) => mine[d]?.length && theirs[d]?.length
  );
  if (mutualDims.length < 2) return null;

  let weightSum = 0;
  let acc = 0;
  for (const d of mutualDims) {
    const overlap = mine[d].filter((tag) => theirs[d].includes(tag)).length;
    const dice = (2 * overlap) / (mine[d].length + theirs[d].length);
    acc += DIMENSION_WEIGHTS[d] * dice;
    weightSum += DIMENSION_WEIGHTS[d];
  }
  return Math.round((acc / weightSum) * 100);
}

// "kindred" | "similar" | null (below TIER_SIMILAR the badge shows shared chips only)
export function getSimilarityTier(score) {
  if (score == null) return null;
  if (score >= TIER_KINDRED) return "kindred";
  if (score >= TIER_SIMILAR) return "similar";
  return null;
}

// Flat list of shared tags, most-meaningful dimension first
export function getSharedStyles(myStyles, theirStyles) {
  return getSharedStylesByDimension(myStyles, theirStyles).flatMap((g) => g.tags);
}

// [{ dimension, tags }] for mutual tags, ordered by dimension weight, empty dims omitted
export function getSharedStylesByDimension(myStyles, theirStyles) {
  if (!myStyles?.length || !theirStyles?.length) return [];
  const mine = groupByDimension(myStyles);
  const theirs = groupByDimension(theirStyles);
  const groups = [];
  for (const d of DIMENSIONS_BY_WEIGHT) {
    if (!mine[d]?.length || !theirs[d]?.length) continue;
    const tags = mine[d].filter((tag) => theirs[d].includes(tag));
    if (tags.length) groups.push({ dimension: d, tags });
  }
  return groups;
}

// Country codes both users have actually pinned in (behavioral evidence)
export function getSharedCountries(myStats, theirStats) {
  const mine = myStats?.countries || [];
  const theirs = theirStats?.countries || [];
  return mine.filter((c) => theirs.includes(c));
}

function cosine(a, b) {
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  if (!na || !nb) return null;
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

// Blend the declared-tag score with observed behavior (what each user actually pins).
// behaviorStats: { categoryCounts: {food, stay, adventure}, countries: [codes], postCount }
// Shrinkage w = n/(n+K) over the smaller post count means the blend degrades exactly
// to the tag score at cold start and leans on behavior as both users post more.
// Returns { score, behaviorUsed, sharedCountries }, or null when tags can't score.
export function calcBlendedSimilarity(me, them) {
  const tagScore = calcSimilarity(me?.travelStyles, them?.travelStyles);
  if (tagScore === null) return null;

  const a = me?.behaviorStats;
  const b = them?.behaviorStats;
  const sharedCountries = getSharedCountries(a, b);
  const n = Math.min(a?.postCount || 0, b?.postCount || 0);
  if (n === 0) return { score: tagScore, behaviorUsed: false, sharedCountries };

  const components = [];
  if (n >= MIN_POSTS_FOR_CATEGORIES) {
    const cats = ["food", "stay", "adventure"];
    const catCosine = cosine(
      cats.map((c) => a.categoryCounts?.[c] || 0),
      cats.map((c) => b.categoryCounts?.[c] || 0)
    );
    if (catCosine !== null) components.push([0.6, catCosine]);
  }
  const minCountries = Math.min(a.countries?.length || 0, b.countries?.length || 0);
  if (minCountries > 0) {
    components.push([0.4, sharedCountries.length / minCountries]);
  }
  if (!components.length) {
    return { score: tagScore, behaviorUsed: false, sharedCountries };
  }

  const weightSum = components.reduce((s, [w]) => s + w, 0);
  const behavior = components.reduce((s, [w, v]) => s + w * v, 0) / weightSum;
  const w = n / (n + BEHAVIOR_K);
  return {
    score: Math.round((1 - w) * tagScore + w * behavior * 100),
    behaviorUsed: true,
    sharedCountries,
  };
}
